/**
 * `/liturgies/{id}/planning` — the planning surface, and the item panel.
 *
 * From the mockup's `isPlanning` block: a sticky section rail, one panel per section, and rows that
 * expand into the item panel with a blue left border.
 *
 * **The four acts are four separate buttons**, and the screen never collapses them. A row shows its
 * current proposal, its review state, and — separately — whether an approved value stands behind it.
 * Save does not submit. Submit does not approve. That is the product's central rule and this is
 * where a user meets it (docs/engineering/03-lifecycle-and-states.md).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearch, useNavigate } from '@tanstack/react-router';
import type { ItemValue } from '@pius/contracts';

import {
  ApiError,
  approveItem,
  fetchLiturgy,
  fetchPlanning,
  requestRevision,
  saveItem,
  submitItem,
  type PlanningItemView,
} from './api';
import { editorFor, summarize } from '../planning/renderers';
import { fetchRoster } from '../people/api';
import { fetchAttachments } from '../attachments/api';
import { Notice } from '../../components/primitives';
import { WorkspaceTabs } from '../../components/composites/WorkspaceTabs';
import './liturgies.css';

const STATE_TONE: Record<string, string> = {
  draft: 'lit-pill--neutral',
  submitted: 'lit-pill--ready',
  under_review: 'lit-pill--ready',
  approved: 'lit-pill--active',
  revision_requested: 'lit-pill--blocking',
  rejected: 'lit-pill--blocking',
  superseded: 'lit-pill--neutral',
};

const STATE_LABEL: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under review',
  approved: 'Approved',
  revision_requested: 'Revision requested',
  rejected: 'Rejected',
  superseded: 'Superseded',
};

export function PlanningScreen(): React.JSX.Element {
  const { id } = useParams({ from: '/liturgies/$id/planning' });
  const { t } = useTranslation('common');
  const search = useSearch({ from: '/liturgies/$id/planning' });
  const navigate = useNavigate({ from: '/liturgies/$id/planning' });

  // Use URL param for initial state
  const [expanded, setExpandedState] = useState<string | null>(search.item ?? null);

  const setExpanded = (itemId: string | null) => {
    setExpandedState(itemId);
    navigate({ search: itemId ? { item: itemId } : {}, replace: true });
  };

  const liturgy = useQuery({ queryKey: ['liturgy', id], queryFn: () => fetchLiturgy(id) });
  const planning = useQuery({ queryKey: ['planning', id], queryFn: () => fetchPlanning(id) });
  // The roster, so a `person_ref` item can offer this celebration's People rather than a text box.
  // Its own query: a workspace with no person_ref item still renders while this is in flight.
  const roster = useQuery({ queryKey: ['roster', id], queryFn: () => fetchRoster(id) });
  // Likewise for `attachment_ref`: the list carries each file's availability, and only an available
  // one may be chosen.
  const files = useQuery({ queryKey: ['attachments', id], queryFn: () => fetchAttachments(id) });

  if (planning.isPending || liturgy.isPending) {
    return <p className="lit-panel__state">{t('liturgies.loading_workspace')}</p>;
  }

  if (planning.isError || liturgy.isError) {
    const error = (planning.error ?? liturgy.error) as ApiError | undefined;
    return (
      <div className="lit-plan">
        <Notice referenceId={error?.correlationId}>
          {error?.message ?? 'This workspace could not be loaded.'}
        </Notice>
      </div>
    );
  }

  const sections = planning.data.sections;
  const editable = liturgy.data.is_planning_editable;

  return (
    <div className="lit-plan">
      <aside className="lit-plan__rail">
        <p className="lit-eyebrow">{t('liturgies.planning_sections')}</p>
        {sections.map((section) => {
          const outstanding = section.items.filter((i) => i.is_constraint && !i.is_approved).length;
          return (
            <a key={section.id} className="lit-plan__rail-item" href={`#section-${section.key}`}>
              {/* Red marks an outstanding Constraint — the only thing that blocks Finalization. */}
              <span
                className={`lit-plan__dot${outstanding > 0 ? ' lit-plan__dot--blocking' : ''}`}
              />
              <span className="lit-plan__rail-label">{section.label}</span>
              <span className="lit-plan__count">{section.items.length}</span>
            </a>
          );
        })}

        <p className="lit-plan__rail-note">{t('planning.template_source_note')}</p>
      </aside>

      <div className="lit-plan__main">
        <header className="lit-plan__head">
          <div>
            <p className="lit-eyebrow">{liturgy.data.place ?? t('workspace.planning')}</p>
            <h1 className="lit-plan__title">{liturgy.data.title}</h1>
          </div>
          <div className="lit-plan__head-acts">
            <Link className="lit-btn lit-btn--quiet" to="/liturgies/$id/exports" params={{ id }}>
              {t('workspace.exports')}
            </Link>
            <Link className="lit-btn lit-btn--quiet" to="/liturgies/$id/access" params={{ id }}>
              {t('access.share.submit', 'Share access')}
            </Link>
            <Link className="lit-btn lit-btn--quiet" to="/liturgies/$id" params={{ id }}>
              {t('workspace.overview')}
            </Link>
          </div>
        </header>

        <WorkspaceTabs liturgyId={id} />

        {!editable ? (
          <Notice tone="success">
            {t('planning.planning_closed_notice', {
              state: t(
                `lifecycle.${liturgy.data.lifecycle_state}`,
                liturgy.data.lifecycle_state.replace(/_/g, ' '),
              ),
            })}
          </Notice>
        ) : null}

        {sections.map((section) => (
          <section key={section.id} className="lit-panel" id={`section-${section.key}`}>
            <div className="lit-panel__head">
              <h2 className="lit-panel__title">{section.label}</h2>
              <span className="lit-panel__meta">
                {t('planning.approved_count', {
                  approved: section.items.filter((i) => i.is_approved).length,
                  total: section.items.length,
                })}
              </span>
            </div>

            {section.items.map((item) => (
              <ItemRow
                key={item.id}
                liturgyId={id}
                item={item}
                editable={editable}
                timezone={liturgy.data.celebration_timezone}
                people={roster.data?.people ?? []}
                attachments={files.data?.data ?? []}
                expanded={expanded === item.id}
                onToggle={() => setExpanded(expanded === item.id ? null : item.id)}
              />
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}

function ItemRow({
  liturgyId,
  item,
  editable,
  timezone,
  people,
  attachments,
  expanded,
  onToggle,
}: {
  liturgyId: string;
  item: PlanningItemView;
  editable: boolean;
  timezone: string | null;
  people: { id: string; display_name: string }[];
  attachments: { id: string; display_name: string; available: boolean }[];
  expanded: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<ItemValue | null>(item.value);
  const [reason, setReason] = useState('');

  const configuration = item.configuration;
  const options = configuration?.options ?? [];
  const accessMode = configuration?.access_mode ?? 'selectable';

  const writable = editable && accessMode === 'selectable';
  const Editor = editorFor(item.item_type);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['planning', liturgyId] });
    await queryClient.invalidateQueries({ queryKey: ['liturgy', liturgyId] });
  };

  const save = useMutation({
    mutationFn: () => saveItem(item.id, draft),
    onSuccess: refresh,
  });
  const submit = useMutation({ mutationFn: () => submitItem(item.id), onSuccess: refresh });
  const approve = useMutation({ mutationFn: () => approveItem(item.id), onSuccess: refresh });
  const revise = useMutation({
    mutationFn: () => requestRevision(item.id, reason),
    onSuccess: refresh,
  });

  const error = [save.error, submit.error, approve.error, revise.error].find(
    (e): e is ApiError => e instanceof ApiError,
  );

  return (
    <>
      <div
        className={`lit-item${item.is_constraint && !item.is_approved ? ' lit-item--blocking' : ''}`}
      >
        <button
          type="button"
          className="lit-item__name"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <span className="lit-item__label">{item.label}</span>
          <span className="lit-item__badges">
            {item.is_constraint ? (
              <span className="lit-badge lit-badge--constraint">{t('liturgies.constraint')}</span>
            ) : null}
            {accessMode !== 'selectable' ? (
              <span className="lit-badge lit-badge--policy">
                {accessMode === 'hidden'
                  ? t('planning.hidden_from_participants')
                  : t('planning.set_by_parish')}
              </span>
            ) : null}
          </span>
        </button>

        <div className="lit-item__value">
          {summarize(item.value, options) || (
            <span className="lit-row__muted">{t('liturgies.not_set')}</span>
          )}
          {item.is_approved && item.state !== 'approved' ? (
            <div className="lit-item__note">{t('liturgies.approved_stands')}</div>
          ) : null}
          {item.note ? <div className="lit-item__note">{item.note}</div> : null}
        </div>

        <div className="lit-item__state">
          <span className={`lit-pill ${STATE_TONE[item.state] ?? 'lit-pill--neutral'}`}>
            {t(`planning_states.${item.state}`, STATE_LABEL[item.state] ?? item.state)}
          </span>
        </div>
      </div>

      {expanded ? (
        <div className="lit-item__panel">
          <div className="lit-item__panel-header">
            <span className="lit-item__panel-title">
              {item.label} · {t('planning.details_and_actions')}
            </span>
            <button
              type="button"
              className="lit-item__panel-close"
              onClick={onToggle}
              aria-label={t('planning.close_panel')}
            >
              ✕ {t('planning.close')}
            </button>
          </div>

          {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

          {item.is_constraint ? (
            <p className="lit-item__help">
              {item.constraint_explanation ?? t('planning.constraint_required_help')}
            </p>
          ) : (
            <p className="lit-item__help">{t('planning.optional_item_help')}</p>
          )}

          {configuration?.note ? (
            <p className="lit-item__policy-note">{configuration.note}</p>
          ) : null}

          {editable && !writable ? (
            <p className="lit-item__help">
              {accessMode === 'hidden'
                ? t('planning.hidden_by_policy')
                : t('planning.reserved_by_policy')}
            </p>
          ) : null}

          {Editor ? (
            <Editor
              itemId={item.id}
              label={item.label}
              value={writable ? draft : item.value}
              options={options}
              disabled={!writable}
              timezone={timezone}
              people={people}
              attachments={attachments}
              onChange={setDraft}
            />
          ) : (
            <p className="lit-item__help">{t('planning.unrecognised_type')}</p>
          )}

          {writable ? (
            <>
              <div className="lit-item__acts">
                <button
                  type="button"
                  className="lit-btn"
                  disabled={save.isPending}
                  onClick={() => save.mutate()}
                >
                  {t('planning.save_draft')}
                </button>

                <button
                  type="button"
                  className="lit-btn lit-btn--secondary"
                  disabled={submit.isPending}
                  onClick={() => submit.mutate()}
                >
                  {t('planning.submit_for_review')}
                </button>

                <button
                  type="button"
                  className="lit-btn lit-btn--secondary"
                  disabled={approve.isPending}
                  onClick={() => approve.mutate()}
                >
                  {t('planning.approve')}
                </button>

                <span className="lit-acts-spacer" style={{ flexGrow: 1 }} />

                <button type="button" className="lit-btn lit-btn--quiet" onClick={onToggle}>
                  {t('planning.close')}
                </button>
              </div>

              <div className="lit-item__acts">
                <label className="lit-item__field lit-item__field--grow">
                  <span className="visually-hidden">{t('liturgies.reason_revision')}</span>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={t('planning.reason_placeholder')}
                  />
                </label>
                <button
                  type="button"
                  className="lit-btn"
                  disabled={revise.isPending || reason.trim().length === 0}
                  onClick={() => revise.mutate()}
                >
                  {t('planning.request_revision')}
                </button>
              </div>

              <p className="lit-item__hint">{t('planning.acts_hint')}</p>
            </>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
